'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from './ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Products', href: '/product-catalog' },
  { label: 'Opportunity', href: '/' },
  { label: 'Academy', href: '/member-dashboard' },
  { label: 'About', href: '/' },
];

export default function CorporateNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 group">
              <AppLogo size={36} />
              <span className={`font-display text-xl font-semibold tracking-wide transition-colors ${scrolled ? 'text-foreground group-hover:text-primary' : 'text-white group-hover:text-amber-200'}`}>
                RICHÉSSE
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks?.map(link => (
                <Link
                  key={`nav-${link?.label}`}
                  href={link?.href}
                  className={`text-sm font-medium transition-colors ${scrolled ? 'text-muted-foreground hover:text-foreground' : 'text-white/90 hover:text-white'}`}
                >
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                aria-label="Toggle theme"
              >
                <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} className={scrolled ? 'text-muted-foreground' : 'text-white/80'} />
              </button>
              {!loading && (
                user ? (
                  <Link
                    href="/member-dashboard"
                    className="btn-primary text-sm font-semibold px-5 py-2.5 rounded-lg"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/sign-up-login-screen"
                      className={`text-sm font-medium px-4 py-2 rounded-lg border transition-colors ${scrolled ? 'border-border hover:bg-muted' : 'border-white/50 text-white hover:bg-white/10'}`}
                    >
                      Login
                    </Link>
                    <Link
                      href="/sign-up-login-screen"
                      className="btn-primary text-sm font-semibold px-5 py-2.5 rounded-lg"
                    >
                      Join Richésse
                    </Link>
                  </>
                )
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                aria-label="Toggle theme"
              >
                <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} className={scrolled ? 'text-muted-foreground' : 'text-white/80'} />
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-9 h-9 flex items-center justify-center"
                aria-label="Toggle mobile menu"
              >
                <div className="relative w-5 h-4 flex flex-col justify-between">
                  <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground' : 'bg-white'} ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
                  <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground' : 'bg-white'} ${mobileOpen ? 'opacity-0' : ''}`} />
                  <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground' : 'bg-white'} ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-96' : 'max-h-0'}`}>
          <div className="bg-background border-t border-border px-6 py-4 space-y-1">
            {navLinks?.map(link => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-sm font-medium text-muted-foreground hover:text-foreground border-b border-border/50 last:border-0 transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-4">
              {!loading && (
                user ? (
                  <Link
                    href="/member-dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="btn-primary text-center text-sm font-semibold py-2.5 rounded-lg"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/sign-up-login-screen"
                      onClick={() => setMobileOpen(false)}
                      className="text-center text-sm font-medium py-2.5 rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      Login
                    </Link>
                    <Link
                      href="/sign-up-login-screen"
                      onClick={() => setMobileOpen(false)}
                      className="btn-primary text-center text-sm font-semibold py-2.5 rounded-lg"
                    >
                      Join Richésse
                    </Link>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}